import { action, computed, observable, runInAction } from 'mobx'
import analytics from '../../../src/stores/analytics'
import { defaultCountry, defaultCurrency } from '../../../shared-lib/i18n/i18n'
import { getSearchConfig } from '../../lib/AddressMatch'
import { $RootStore } from '../../../src/stores/RootStore'
import { UserFieldsFragment } from '../../../graphQL/generated/client'
import { WorkplaceBusinessDetails } from '../../lib/workplaces'
import { clearGraphQLCache } from '../../lib/rootStoreLifecycle'

export interface OnboardingData {
  onboardingId: string
  businessName: string
  trialLength: number
  referrer: string
  inviteCode: string
  salonBusinessName: string
  isSalonSignup: boolean
  countryISOCode?: string
}

const defaultOnboardingData = {
  onboardingId: '',
  businessName: '',
  trialLength: 30,
  referrer: '',
  inviteCode: '',
  salonBusinessName: '',
  isSalonSignup: false,
}

const EMPTY_ROLE = '' as const
const SALON_SESSION_ROLES: readonly AuthRole[] = ['SALON', 'SALON_ADMIN', 'SALON_RECEPTIONIST']
const SALON_OWNER_OR_ADMIN_ROLES: readonly AuthRole[] = ['SALON', 'SALON_ADMIN']
const RECEPTION_ROLES: readonly AuthRole[] = ['SALON_RECEPTIONIST']
const ASSUMED_ADMIN_ROLES: readonly AuthRole[] = ['SALON_ADMIN']
const SOLO_ROLES: readonly AuthRole[] = ['SAAS', ...SALON_SESSION_ROLES]
const APP_ROLES: readonly AuthRole[] = ['PROFESSIONAL', ...SOLO_ROLES]

const hasRole = (roles: readonly AuthRole[], role: AuthRole | typeof EMPTY_ROLE | null | undefined) =>
  !!role && roles.includes(role)

export interface AuthStoreToken {
  role: AuthRole | typeof EMPTY_ROLE
  auth: string
  expires?: string
  locallyexpiredtoken?: boolean
}

const emptyAuthStoreToken = {
  auth: '',
  role: EMPTY_ROLE,
}

export default class AuthStore {

  @observable accessor authToken: AuthStoreToken = {
    ...emptyAuthStoreToken,
  }

  @observable accessor loading = false
  // Owner whose data the UI is currently scoped to (e.g. the salon owning the booking
  // being viewed). Null = the logged in user's own data. Purely client-side: the API
  // returns everything the user can access and stores filter their items by this id
  // (see ExternalTreatmentStore.scopedItems). Transient — not persisted to storage.
  @observable accessor dataScopeId: string | null = null

  codeSentAt!: number
  rootStore: $RootStore
  authFlow: 'signIn' | 'createAccount' | undefined
  @observable accessor sessionExpired = false
  /*
    Store name of the person behind an assumed salon session. The UI uses it on switch back to name account.
  */
  @observable accessor assumedFromName = ''
  @observable accessor onboardingData: OnboardingData = {
    ...defaultOnboardingData,
  }

  professionalId?: string
  getAuthToken: (professionalId: string) => Promise<void> = async () => { }

  constructor(rootStore: $RootStore) {
    this.rootStore = rootStore
  }

  @action.bound setAuthToken(authToken: AuthStoreToken | null | undefined) {
    this.authToken = authToken ?? emptyAuthStoreToken
  }

  // Low-level setter. Within a booking the scope is set directly only when opening (loadData)
  // or clearing; salon switches go through BaseViewExternalBookingVM.requestScopeChange so the
  // scoped client/treatments can't be silently invalidated.
  @action.bound setDataScopeId(scopeId: string | null) {
    this.dataScopeId = scopeId
  }

  @action.bound setAuthFlow(mode: 'signIn' | 'createAccount') {
    this.authFlow = mode
  }

  @computed get isSalonSession(): boolean {
    return hasRole(SALON_SESSION_ROLES, this.authToken.role)
  }

  @computed get isSalonOwnerOrAdminSession(): boolean {
    return hasRole(SALON_OWNER_OR_ADMIN_ROLES, this.authToken.role)
  }

  @computed get isReceptionSession(): boolean {
    return hasRole(RECEPTION_ROLES, this.authToken.role)
  }

  @computed get isAssumedAdminSession(): boolean {
    return hasRole(ASSUMED_ADMIN_ROLES, this.authToken.role)
  }

  @computed get activeSalonId(): string | null {
    return this.isSalonSession ? this.rootStore.userStore.data._id || null : null
  }

  @action.bound private rememberPersonBehindSalon() {
    if (this.isSalonSession) return

    const { firstName, lastName } = this.rootStore.userStore.data
    this.assumedFromName = `${firstName ?? ''} ${lastName ?? ''}`.trim()
  }

  @action.bound async signInAsSalon_api(salonId: string): Promise<boolean> {
    const { communication: { requester } } = this.rootStore

    this.loading = true

    this.rememberPersonBehindSalon()

    try {
      if (this.isSalonSession) {
        const { signOutOfSalon } = await requester.signOutOfSalon()
        if (!signOutOfSalon?.authToken) return false
        // Only the token needs swapping here — the personal session is a stepping stone, so its
        // data is never loaded.
        this.setAuthToken(signOutOfSalon.authToken)
      }

      const { signInAsSalon } = await requester.signInAsSalon({ salonId })
      if (!signInAsSalon?.authToken || !signInAsSalon.user) return false

      await this.switchAuthenticatedAccount(signInAsSalon.authToken, signInAsSalon.user)
      return true
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return false
    } finally {
      this.loading = false
    }
  }

  @action.bound async createSalon_api(details: WorkplaceBusinessDetails): Promise<string | null> {
    const { communication: { requester } } = this.rootStore

    this.loading = true

    this.rememberPersonBehindSalon()

    try {
      const { createSalon } = await requester.createSalon(details)
      if (!createSalon?.authToken || !createSalon.user) return null

      await this.switchAuthenticatedAccount(createSalon.authToken, createSalon.user)
      return createSalon.user._id
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return null
    } finally {
      this.loading = false
    }
  }

  @action.bound async signOutOfSalon_api(): Promise<boolean> {
    const { communication: { requester } } = this.rootStore

    this.loading = true

    try {
      const { signOutOfSalon } = await requester.signOutOfSalon()
      if (!signOutOfSalon?.authToken || !signOutOfSalon.user) return false

      await this.switchAuthenticatedAccount(signOutOfSalon.authToken, signOutOfSalon.user)
      runInAction(() => {
        this.assumedFromName = ''
      })
      return true
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return false
    } finally {
      this.loading = false
    }
  }

  @action.bound async finishAuthFlow({ loadSessionIfNotSalon = true }: { loadSessionIfNotSalon?: boolean } = {}) {
    const { afterAuthenticated } = this.rootStore

    this.sessionExpired = false
    this.authFlow = undefined
    this.syncInStorage()

    if (loadSessionIfNotSalon) await afterAuthenticated()

    analytics.track('Pro Logged Into App')
  }

  @action.bound async identifyWithMobile_api(mobile: string): Promise<boolean> {
    const { communication: { requester } } = this.rootStore

    this.loading = true

    let response = null
    try {
      response = await requester.identifyWithMobile({ mobile })

    } catch (e) {
      throw e
    } finally {
      this.loading = false
    }

    const data = response.identifyWithMobile
    if (!data || !data.user || !data.authToken) return false

    const { authToken, user } = data

    if (!hasRole(APP_ROLES, user.role)) {
      return false
    }

    this.setAuthToken(authToken)

    return !!this.authToken
  }

  @action.bound async sendMobileVerification_api(mobile: string): Promise<boolean> {
    const { communication: { requester }, uiStore, i18n: { s } } = this.rootStore

    this.loading = true

    try {
      const { sendMobileVerification } = await requester.sendMobileVerification({ mobile })
      this.codeSentAt = Date.now()

      if (!sendMobileVerification || !sendMobileVerification.authToken) return false

      const { authToken } = sendMobileVerification
      this.setAuthToken(authToken)
      return !!authToken
    } catch (e: any) {
      if (e.message.includes('blocked by Verify Geo-Permissions')) {
        uiStore.showMessage({
          title: s('ERROR_SENDING_VERIFICATION_TITLE'),
          body: s('SMS_GEO_BLOCKED', mobile),
          important: true,
        })
      } else {
        uiStore.showMessage({
          title: s('ERROR_SENDING_VERIFICATION_TITLE'),
          body: s('ERROR_SENDING_VERIFICATION_BODY', mobile, e.message ?? ''),
          important: true,
        })
      }
      return false
    } finally {
      this.loading = false
    }
  }

  @action.bound async verifyMobileAndSignIn_api(mobile: string, code: string): Promise<boolean> {
    const { communication: { requester }, userStore } = this.rootStore

    this.loading = true

    try {
      const response = await requester.verifyMobileAndSignIn({ mobile, code })

      const data = response?.verifyMobileAndSignIn
      if (!data || !data.user || !data.authToken) return false

      const { authToken, user } = data

      userStore.merge(user)
      this.setAuthToken(authToken)
      this.assumedFromName = ''
      await this.finishAuthFlow({ loadSessionIfNotSalon: false })

      return !!authToken
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return false
    } finally {
      this.loading = false
    }
  }

  @action.bound applyUserLocale(user: Pick<UserFieldsFragment, 'currencyCode' | 'countryISOCode'> | null | undefined) {
    const { i18n, config } = this.rootStore

    const newCurrency = user?.currencyCode ?? defaultCurrency
    i18n.setCurrency(newCurrency)
    analytics.config.currency = newCurrency
    i18n.setCountry(user?.countryISOCode ?? defaultCountry)
    config.addressSearchCountry = getSearchConfig(user?.countryISOCode)
  }

  @action.bound async verifyMobileAndCompleteAccount_api(mobile: string, code: string, userData: RegistrationDetails): Promise<boolean> {
    const { communication: { requester }, userStore } = this.rootStore

    this.loading = true

    try {
      const response = await requester.verifyMobileAndCompleteAccount({
        mobile,
        code,
        userData: userData,
        onboardingId: this.onboardingData.onboardingId,
        trialLength: this.onboardingData.trialLength,
      })

      const data = response.verifyMobileAndCompleteAccount
      if (!data) return false

      userStore.merge(data.user)
      this.setAuthToken(data.authToken)

      if (this.onboardingData.referrer) {
        await requester.addReferralToUser({ referredBy: this.onboardingData.referrer })
      }

      this.applyUserLocale(data.user)

      this.finishAuthFlow()

      return !!this.authToken
    } catch (e) {
      return false
    } finally {
      this.loading = false
    }
  }

  @action.bound async verifyMobileAndCreateSalon_api(mobile: string, code: string, userData: RegistrationDetails): Promise<boolean> {
    const { communication: { requester }, userStore } = this.rootStore

    this.loading = true

    try {
      const response = await requester.verifyMobileAndCreateSalon({
        mobile,
        code,
        userData: userData,
        onboardingId: this.onboardingData.onboardingId,
        trialLength: this.onboardingData.trialLength,
      })

      const data = response.verifyMobileAndCreateSalon
      if (!data) return false

      userStore.merge(data.user)
      this.setAuthToken(data.authToken)

      this.applyUserLocale(data.user)

      this.finishAuthFlow()

      return !!this.authToken
    } catch (e) {
      return false
    } finally {
      this.loading = false
    }
  }

  @action.bound async identifyWithInviteCode_api(inviteCode: string, mobile: string): Promise<boolean> {
    const { communication: { requester } } = this.rootStore

    this.loading = true

    try {
      const { identifyWithInviteCode } = await requester.identifyWithInviteCode({ inviteCode, mobile })

      if (!identifyWithInviteCode?.authToken) return false

      this.setAuthToken(identifyWithInviteCode.authToken)

      return true
    } finally {
      this.loading = false
    }
  }

  @action.bound async verifyMobileAndJoinSalon_api(
    inviteCode: string,
    mobile: string,
    code: string,
    userData: SalonMemberDetails,
  ): Promise<boolean> {
    const { communication: { requester }, userStore } = this.rootStore

    this.loading = true

    try {
      const { verifyMobileAndJoinSalon } = await requester.verifyMobileAndJoinSalon({
        inviteCode,
        mobile,
        code,
        userData,
      })

      if (!verifyMobileAndJoinSalon) return false

      userStore.merge(verifyMobileAndJoinSalon.user)
      this.setAuthToken(verifyMobileAndJoinSalon.authToken)

      this.applyUserLocale(verifyMobileAndJoinSalon.user)

      await this.finishAuthFlow()

      return !!this.authToken
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return false
    } finally {
      this.loading = false
    }
  }

  @action.bound async createNewSaasUserWithMobile_api(mobile: string) {
    const { communication: { requester } } = this.rootStore

    this.loading = true

    try {
      const response = await requester.createNewSaasUserWithMobile({ mobile })
      const data = response.createNewSaasUserWithMobile
      if (!data) return false

      const { authToken } = data
      this.setAuthToken(authToken)

      return !!authToken
    } catch (e) {
      return false
    } finally {
      this.loading = false
    }
  }

  @action.bound async verifyMagicLinkToken_api(token: string) {
    const { communication: { requester }, userStore, clearAllData } = this.rootStore

    this.loading = true

    try {
      const { verifyMagicLinkToken } = await requester.verifyMagicLinkToken({ token })
      clearAllData()

      this.setAuthToken(verifyMagicLinkToken.authToken)
      userStore.merge(verifyMagicLinkToken.user)
      await this.finishAuthFlow({ loadSessionIfNotSalon: false })

      return true
    } catch (e) {
      return false
    } finally {
      this.loading = false
    }
  }

  @action.bound async sendMagicLinkEmail_api(email: string, adminEmail?: string): Promise<boolean> {
    const { communication: { requester } } = this.rootStore

    try {
      const response = await requester.sendMagicLinkEmail({ email, adminEmail })
      return response.sendMagicLinkEmail
    } catch (e) {
      return false
    }
  }

  @action.bound setSessionExpired = (expired: boolean) => {
    this.sessionExpired = expired
    this.syncInStorage()
  }

  expireToken = () => {
    this.authToken.locallyexpiredtoken = true
    this.syncInStorage()
  }

  @action.bound syncInStorage = (): void => {
    this.rootStore.storeDataSync.saveStoreToStorage('AUTH')
  }

  @action.bound async checkBlockedUser_api(): Promise<boolean> {
    const { communication: { requester } } = this.rootStore

    try {
      const ret = await requester.checkBlockedUser({})
      const checkBlockedUser = ret.checkBlockedUser

      if (checkBlockedUser) {
        await this.logout_api()
        return checkBlockedUser
      }

      return false
    } catch (e: unknown) {
      return false
    }
  }

  @action.bound async logout_api(): Promise<boolean> {
    const { userDeviceStore, communication: { requester }, clearAllData } = this.rootStore
    const uniqueDeviceId = userDeviceStore.data.uniqueDeviceId

    try {
      const ret = await requester.logout({ uniqueDeviceId })
      return ret.logout || false
    } catch (e) {
      return false
    } finally {
      clearAllData()
      runInAction(() => {
        this.assumedFromName = ''
      })
      await clearGraphQLCache(this.rootStore)
      await this.clearHttpCookies()
    }
  }

  @action.bound async checkMinVersion_api(): Promise<any> {
    const { communication: { requester } } = this.rootStore

    try {
      const ret = await requester.appVer({})
      return ret.appVer
    } catch (e) {
      return null
    }
  }

  @action.bound inflate(data: any) {
    if (data.authToken !== undefined) this.authToken = data.authToken
    if (data.sessionExpired !== undefined) this.sessionExpired = data.sessionExpired
    if (data.onboardingData !== undefined) this.onboardingData = data.onboardingData
    if (data.assumedFromName !== undefined) this.assumedFromName = data.assumedFromName
  }

  @action.bound async switchAuthenticatedAccount(authToken: AuthStoreToken, user: UserFieldsFragment) {
    this.rootStore.clearAllData()

    this.rootStore.userStore.merge(user)
    this.setAuthToken(authToken)
    this.syncInStorage()

    await this.rootStore.afterAuthenticated()
  }

  @computed get isLoggedIn(): boolean {
    return hasRole(APP_ROLES, this.authToken.role) && !this.sessionExpired
  }

  @computed get isSaasUser(): boolean {
    return hasRole(SOLO_ROLES, this.authToken.role)
  }

  @action.bound setOnboardingData = (onboardingData: OnboardingData) => {
    this.onboardingData = onboardingData
    this.syncInStorage()
  }

  @action.bound resetOnboardingData = () => {
    this.onboardingData = { ...defaultOnboardingData }
  }

  getAccessAuthToken(_operationName?: string) {
    return this.authToken
  }

  clearHttpCookies = async () => {
    const { apiUrl } = this.rootStore.communication.config
    try {
      await fetch(`${apiUrl}/clearHttpCookies`, { method: 'POST', credentials: 'include', headers: { platform: this.rootStore.config.platform } })
    } catch (e: any) {
      console.error(e)
      this.rootStore.errorHandler.showErrorInUI(e)
    }

    this.setAuthToken(null)
    this.syncInStorage()
  }

  clearData() {
    this.setAuthToken(null)
    this.dataScopeId = null
    this.resetOnboardingData()
    this.syncInStorage()
  }

  serialize() {
    return {
      authToken: this.authToken,
      onboardingData: this.onboardingData,
      assumedFromName: this.assumedFromName,
    }
  }
}
